import { observer } from 'mobx-react-lite'
import { type BillingMethodListItemViewModel } from '@/features/billing/profile/BillingMethodList/BillingMethodListItemViewModel'
import { type BillingMethodListViewModel } from '@/features/billing/profile/BillingMethodList/BillingMethodListViewModel'
import {
  BillingMethodList,
  BillingMethodListItem,
  type BillingMethodListProps,
} from '@/features/billing/profile/BillingMethodList'
import { SkeletonList } from '@/components/Skeleton'
import { BillingMethodListItemSkeleton } from '@/features/billing/profile/BillingMethodList'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { useRootStore } from '@/contexts/root-store-context'
import { Button } from '@/components/Button'

export interface BillingMethodListContainerProps {
  vm: BillingMethodListViewModel
  'aria-labelledby': string
  mode?: BillingMethodListProps['mode']
  isDisabled?: boolean
}

/**
 * Container component that manages billing methods ViewModels and renders the list.
 */
export const BillingMethodListContainer = observer(
  function BillingMethodListContainer({
    vm,
    mode = 'default',
    isDisabled,
    ...rest
  }: BillingMethodListContainerProps) {
    const isProfileLoading = vm.fetchProfile.pending
    const methodVms = vm.methods.items
    isDisabled ||= methodVms.some((v) => v.setAsDefault.pending)

    return (
      <>
        <BillingMethodList mode={mode} {...rest}>
          {isProfileLoading ? (
            <SkeletonList count={2}>
              <BillingMethodListItemSkeleton />
            </SkeletonList>
          ) : (
            methodVms.map((methodVm) => (
              <BillingMethodListItemContainer
                vm={methodVm}
                isDisabled={isDisabled}
                id={methodVm.id}
                key={methodVm.id}
              />
            ))
          )}
        </BillingMethodList>
        {methodVms.map((methodVm) => (
          <ConfirmRemoveMethodDialog key={methodVm.id} vm={methodVm} />
        ))}
      </>
    )
  },
)

interface BillingMethodListItemContainerProps {
  vm: BillingMethodListItemViewModel
  isDisabled?: boolean
  id?: string
}

/**
 * Container component that manages ViewModel logic for billing method items.
 */
export const BillingMethodListItemContainer = observer(
  function BillingMethodListItemContainer({
    vm,
    isDisabled,
  }: BillingMethodListItemContainerProps) {
    return (
      <BillingMethodListItem
        cardBrand={vm.cardBrand}
        cardLast4={vm.cardLast4}
        cardExpMonth={vm.cardExpMonth}
        cardExpYear={vm.cardExpYear}
        isDefault={vm.isDefault}
        isDisabled={isDisabled || vm.setAsDefault.pending}
        onSetAsDefault={vm.setAsDefault}
        onShowRemoveDialog={vm.removeModal.show}
        isPending={vm.setAsDefault.pending}
      />
    )
  },
)

interface ConfirmRemoveMethodDialogProps {
  vm: BillingMethodListItemViewModel
}

/**
 * Confirm Remove Method Dialog
 */
export const ConfirmRemoveMethodDialog = observer(
  function ConfirmRemoveMethodDialog({ vm }: ConfirmRemoveMethodDialogProps) {
    const {
      shell: { shellInfo },
    } = useRootStore()

    return (
      <ModalDialog
        isDismissable={!vm.remove.pending}
        isOpen={vm.removeModal.showing}
        onClose={vm.removeModal.close}
      >
        <ModalDialogHeader>
          <ModalDialogTitle>Remove payment method?</ModalDialogTitle>
          <ModalDialogDescription>
            This payment method will be removed from your {shellInfo.name}{' '}
            account.
          </ModalDialogDescription>
        </ModalDialogHeader>
        <ModalDialogFooter>
          <ModalDialogActions>
            <Button
              label="Remove"
              onPress={vm.remove}
              isPending={vm.remove.pending}
            />
            <Button
              label="Cancel"
              variant="outline"
              onPress={vm.removeModal.close}
              isDisabled={vm.remove.pending}
            />
          </ModalDialogActions>
        </ModalDialogFooter>
      </ModalDialog>
    )
  },
)
